import JSZip from 'jszip';
import GeoJSON from 'ol/format/GeoJSON';
import CKAN from '../lib/CKAN';
import { exportArcGISTilesClipped } from '../map/utils';

const HUB_DOWNLOAD_API = 'https://hub.arcgis.com/api/download/v1/items';
const HUB_POLL_MS = 3000;
const HUB_TIMEOUT_MS = 10 * 60 * 1000;

const triggerDownload = (href, filename) => {
  const link = document.createElement('a');
  link.href = href;
  if (filename) link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The ArcGIS Online item and layer id behind a FeatureServer layer URL.
const getArcGISLayerItem = async (layerUrl) => {
  const match = layerUrl.match(/^(.*\/FeatureServer)\/(\d+)\/?$/);
  if (!match) throw new Error('Unrecognized ArcGIS layer URL.');
  const meta = await fetch(`${match[1]}?f=json`).then((r) => r.json());
  if (!meta.serviceItemId) {
    throw new Error('This ArcGIS service has no item to download from.');
  }
  return { itemId: meta.serviceItemId, layerId: match[2] };
};

// Asks ArcGIS Hub for a shapefile (zip) of one layer and waits while Hub
// generates it - the first request for a layer can take a minute or more,
// later ones reuse Hub's cached file. Resolves to the file's URL.
const requestHubShapefile = async (layerUrl, onProgress) => {
  const { itemId, layerId } = await getArcGISLayerItem(layerUrl);
  const url = `${HUB_DOWNLOAD_API}/${itemId}/shapefile?redirect=false&layers=${layerId}`;
  const started = Date.now();
  for (;;) {
    const response = await fetch(url);
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `ArcGIS Hub returned ${response.status}.`);
    }
    if (json.status === 'Completed' && json.resultUrl) return json.resultUrl;
    if (/fail|error/i.test(json.status || '')) {
      throw new Error(json.message || 'ArcGIS Hub could not generate the file.');
    }
    if (Date.now() - started > HUB_TIMEOUT_MS) {
      throw new Error('Timed out waiting for ArcGIS Hub to prepare the file.');
    }
    onProgress(json.progressInPercent);
    await sleep(HUB_POLL_MS);
  }
};

// Cached tile services have no feature data, so the download is the map
// image over the dataset's whole ingested extent, as a zipped GeoTIFF.
const downloadTileLayerImage = async (dataset, resource) => {
  const spatial = dataset.extras?.find((e) => e.key === 'spatial');
  if (!spatial) throw new Error('This dataset has no extent to export.');
  const geometry = new GeoJSON().readGeometry(JSON.parse(spatial.value));
  const toNumber = (value) =>
    value === undefined || value === '' ? undefined : Number(value);
  const tiff = await exportArcGISTilesClipped({
    gis_service: {
      tile_url: resource.url,
      min_zoom: toNumber(resource.min_zoom),
      max_zoom: toNumber(resource.max_zoom),
      extent_4326: geometry.getExtent(),
    },
    clipGeometry: geometry,
    clipProjection: 'EPSG:4326',
  });
  if (!tiff) throw new Error('No map tiles were found for this layer.');
  const zip = new JSZip();
  zip.file(`${dataset.name}.tif`, tiff);
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const href = URL.createObjectURL(blob);
  triggerDownload(href, `${dataset.name}.zip`);
  setTimeout(() => URL.revokeObjectURL(href), 60000);
};

// Downloads a dataset's data. `onStatus` receives progress text for slow
// downloads (ArcGIS layers). Throws an Error with a user-facing message when
// the download fails or the dataset has nothing downloadable.
const DataDownload = async (dataset, { onStatus = () => {} } = {}) => {
  let geoTiffIndex = null;
  let gdbIndex = null;
  let shpIndex = null;
  let wfsIndex = null;
  dataset.resources.forEach((data, i) => {
    if (data.format === 'GeoTiff' || data.format === 'geotiff') {
      geoTiffIndex = i;
    } else if (data.format === 'GDB' || data.format === 'gdb') {
      gdbIndex = i;
    } else if (data.format === 'SHP') {
      shpIndex = i;
    } else if (data.format === 'WFS') {
      wfsIndex = i;
    }
  });
  const arcgisLayers = dataset.resources.filter(
    (data) => data.format === 'ArcGIS Feature Service'
  );
  const tileLayer = dataset.resources.find(
    (data) => data.format === 'ArcGIS Tile Layer'
  );
  if (geoTiffIndex != null) {
    const link = document.createElement('a');
    link.href = dataset.resources[geoTiffIndex].url;
    link.download = dataset.resources[geoTiffIndex].name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else if (gdbIndex !== null) {
    const link = document.createElement('a');
    link.href = dataset.resources[gdbIndex].url;
    link.download = dataset.resources[gdbIndex].name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else if (shpIndex !== null) {
    const link = document.createElement('a');
    link.href = dataset.resources[shpIndex].url;
    link.download = dataset.resources[shpIndex].name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // For boundary data
  } else if (wfsIndex !== null) {
    const wfsData = await CKAN.getWfsBoundaryData(
      dataset.resources[wfsIndex].url,
      dataset.resources[wfsIndex].wfs_feature_id
    );
    if (wfsData.status === 200) {
      const zipStream = new CompressionStream('gzip');
      const blob = new Blob([wfsData.data], { type: 'application/json' });
      const compressedStream = blob.stream().pipeThrough(zipStream);
      const compressedResponse = new Response(compressedStream);
      const zipBlob = await compressedResponse.blob();

      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `${dataset.resources[wfsIndex].wfs_feature_id}.geojson.gz`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.error('Failed to fetch data', wfsData.status);
      throw new Error('The download failed. Please try again later.');
    }
  } else if (arcgisLayers.length > 0) {
    // One shapefile zip per layer.
    for (let i = 0; i < arcgisLayers.length; i++) {
      const layer = arcgisLayers[i];
      const label =
        arcgisLayers.length > 1
          ? `${layer.name} (${i + 1} of ${arcgisLayers.length})`
          : layer.name;
      onStatus(`Preparing ${label}…`);
      const fileUrl = await requestHubShapefile(layer.url, (percent) =>
        onStatus(
          `Preparing ${label}…${percent !== undefined ? ` ${percent}%` : ''}`
        )
      );
      triggerDownload(fileUrl);
    }
  } else if (tileLayer) {
    onStatus('Building map image…');
    await downloadTileLayerImage(dataset, tileLayer);
  } else {
    throw new Error('This dataset has no downloadable file.');
  }
};
export default DataDownload;
